/**
 * Query param carrying where to return after login, email verification or
 * profile completion. The return target travels in the URL rather than in
 * navigation state: canonicalizing redirects replace or drop the state, while
 * they already have to preserve the query.
 */
const REDIRECT_PARAM = 'redirect';

// Steps of signing in itself: returning to one after login would nest or loop.
const AUTH_PAGES = ['login', 'email-verification'];

/**
 * Whether `path` is a same-origin path that is safe to navigate to. Rejects
 * `//host`, `/\host` (browsers read a backslash as a slash), any whitespace
 * (the URL parser strips tabs and newlines, so `/\t/host` becomes `//host`)
 * and anything carrying a scheme.
 */
function isSafeRedirectPath(path: string): boolean {
  return /^\/(?![/\\])/.test(path) && !/\s/.test(path) && !path.includes('://');
}

/** The `redirect` param, when present and a safe same-origin path. */
export function readRedirectParam(
  searchParams: URLSearchParams
): string | undefined {
  const path = searchParams.get(REDIRECT_PARAM);
  return path !== null && isSafeRedirectPath(path) ? path : undefined;
}

/** Append `returnPath` to `path` as the `redirect` query param. */
export function buildPathWithRedirect(
  path: string,
  returnPath: string
): string {
  return `${path}?${REDIRECT_PARAM}=${encodeURIComponent(returnPath)}`;
}

/**
 * Where login should return to when the session ends on `pathname` +
 * `search` (a locale-prefixed app path). An auth page is not a destination,
 * so its own return target is carried over instead.
 */
export function resolveLoginReturnPath(
  pathname: string,
  search: string
): string | undefined {
  const page = pathname.split('/')[2];
  return AUTH_PAGES.includes(page)
    ? readRedirectParam(new URLSearchParams(search))
    : pathname + search;
}
